import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTheme } from 'styled-components';

import { InputStructure } from '../structures/InputStructure';
import { SelectBlockProps, SelectOption } from './types';
import { ImageComponent } from '@/components/Image';
import { ModalSelect } from '@/components/ModalSelect';
import { ScrollBlock } from '@/components/ScrollBlock';
import { ThemeMode } from '@/helpers/enums';
import { getSelectOptionsHeight } from '@/helpers/primitives';
import { useComponentVisible } from '@/hooks/visibility';
import { useCommonStore } from '@/store';

import {
  ScOptions,
  ScOptionsProps,
  ScSelectBlock,
  ScSelectNative,
  ScSelectSearch,
} from './styled';

export const SelectBlock = forwardRef(
  (
    {
      placeholder,
      options,
      selected,
      error,
      hideInputErrorColor,
      hideErrorMessage,
      onSelectOption,
      disabled,
      defaultValue,
      dropdownUp,
      native,
      search,
      cols,
      openOptionsInPortal,
      mode,
      isInputAddon,
      alignBottom,
      ...scProps
    }: SelectBlockProps,
    forwardedRef?: React.Ref<HTMLDivElement>
  ) => {
    const { ref, openerRef, isComponentVisible, setIsComponentVisible } =
      useComponentVisible(false);
    const isMobileDevice = useCommonStore.use.isMobileDevice();

    const handleOpen = useCallback(() => {
      if (!disabled) {
        setIsComponentVisible(!isComponentVisible);
      }
    }, [disabled, isComponentVisible, setIsComponentVisible]);

    const { images } = useTheme();

    const selectedOption = useMemo(
      () => options.find(({ value }) => value === selected),
      [selected, options]
    );

    return (
      <InputStructure error={error} hideErrorMessage={hideErrorMessage}>
        {native ? (
          <ScSelectNative error={!!error}>
            <select
              disabled={disabled}
              onChange={(e) => {
                onSelectOption && onSelectOption(e.target.value);
              }}
            >
              <option disabled selected hidden>
                {placeholder}
              </option>
              {options.map(({ value, name }) => (
                <option
                  key={value}
                  value={value}
                  selected={selectedOption?.value === value}
                >
                  {name}
                </option>
              ))}
            </select>
            <div className="arrow">
              <images.arrowUpGrayIcon.component />
            </div>
          </ScSelectNative>
        ) : (
          <ScSelectBlock
            error={error != null && error !== ''}
            hideInputErrorColor={hideInputErrorColor}
            disabled={!!disabled}
            active={isComponentVisible}
            ref={forwardedRef}
            className={isComponentVisible ? 'active' : ''}
            mode={mode}
            isInputAddon={isInputAddon}
          >
            <div ref={openerRef} className="select-button" onClick={handleOpen}>
              <div
                className={`value${selectedOption?.icon ? ` with-icon` : ''}`}
              >
                {selectedOption?.icon && (
                  <ImageComponent
                    key={selectedOption.value}
                    src={selectedOption.icon}
                    height={24}
                    width={24}
                    alt="option"
                  />
                )}
                {!selectedOption
                  ? defaultValue
                  : (selectedOption.nameV ?? selectedOption.name)}
                {!selectedOption && !defaultValue && !!placeholder && (
                  <span className="placeholder">{placeholder}</span>
                )}
              </div>
              <div className="arrow">
                <images.arrowUpGrayIcon.component />
              </div>
            </div>
            {isComponentVisible &&
              (isMobileDevice ? (
                <ModalSelect
                  cols={cols}
                  title={placeholder}
                  options={options}
                  onChange={(value) => {
                    onSelectOption && onSelectOption(value);
                    handleOpen();
                  }}
                  onClose={handleOpen}
                  borderRoundIconOption
                  search={search}
                  openOptionsInPortal={openOptionsInPortal}
                  isPortal
                  activeOption={selectedOption}
                  alignBottom={alignBottom}
                />
              ) : (
                <OptionsBlock
                  ref={ref}
                  selectedOption={selectedOption}
                  options={options}
                  onChange={(value) => {
                    onSelectOption && onSelectOption(value);
                    handleOpen();
                  }}
                  dropdownUp={dropdownUp}
                  search={search}
                  mode={mode}
                  {...scProps}
                />
              ))}
          </ScSelectBlock>
        )}
      </InputStructure>
    );
  }
);

interface OptionsBlockProps extends ScOptionsProps {
  options: SelectOption[];
  selectedOption?: SelectOption;
  onChange: (value: SelectOption['value']) => void;
  search?: boolean;
  mode?: ThemeMode;
  scrollBlockTopMargin?: number;
}

export const OptionsBlock = forwardRef<HTMLDivElement, OptionsBlockProps>(
  (
    {
      options,
      selectedOption,
      onChange,
      search,
      mode,
      scrollBlockTopMargin,
      ...props
    },
    forwardedRef
  ) => {
    const [searchValue, setSearchValue] = useState('');
    const { images } = useTheme();

    const ref = useRef<HTMLInputElement>(null);

    useEffect(() => {
      if (ref.current == null || search == null) return;

      ref.current.focus();
    }, [search]);

    const handleChangeSearchValue = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchValue(e.target.value);
      },
      [setSearchValue]
    );

    const filteredOptions = useMemo(() => {
      if (searchValue === '' || !search) return options;

      return options.filter(
        ({ nameS }) =>
          !nameS || nameS.toLowerCase().includes(searchValue.toLowerCase())
      );
    }, [searchValue, options, search]);

    return (
      <ScOptions {...props} search={search} ref={forwardedRef}>
        {search && (
          <ScSelectSearch>
            <input
              ref={ref}
              type="text"
              placeholder="Search..."
              onChange={handleChangeSearchValue}
            />
          </ScSelectSearch>
        )}
        {!filteredOptions.length ? (
          <ul>
            <li className="not-found">Not found</li>
          </ul>
        ) : (
          <ScrollBlock
            height={getSelectOptionsHeight(filteredOptions.length, !!search)}
            mode={mode}
            scrollTopMargin={scrollBlockTopMargin}
          >
            <ul>
              {filteredOptions.map(({ icon, name, value }, index) => (
                <li
                  key={index}
                  className={`${selectedOption?.value === value && 'active'} ${
                    icon != null && 'with-icon'
                  }`}
                  onClick={() => onChange(value)}
                >
                  <div className="content">
                    {icon && (
                      <ImageComponent
                        key={value}
                        src={icon}
                        height={24}
                        width={24}
                        alt={`${name}`}
                      />
                    )}
                    {name}
                  </div>
                  {selectedOption?.value === value && (
                    <images.tickCircleIcon.component />
                  )}
                </li>
              ))}
            </ul>
          </ScrollBlock>
        )}
      </ScOptions>
    );
  }
);
